/**
 * Layer-row vocabulary — spec §F5.2 ("`source · kind` small line") and the pane's live
 * *Filter layers* input.
 *
 * Split out of `LayersPane.tsx` because both are pure string logic over the §D5 payload
 * and are worth pinning directly: the label is what a reader uses to tell a raster from a
 * WMS at a glance, and the filter predicate decides which accordions open.
 */
import type { Layer, LayerKind } from '../../lib/api';

/**
 * §F5.2's kind labels, the mockup's wording. The mockup carried its own `type` vocabulary
 * (`raster | points | lines`) and printed `raster / COG` for the first; §D5 splits
 * "raster" into the two ways a raster actually reaches the browser, so `wms_external`
 * gets its own label rather than being flattened back into "raster / COG" — the two
 * differ in who serves the pixels, which is exactly what this line is for.
 */
export const KIND_LABELS: Record<LayerKind, string> = {
  cog_titiler: 'raster / COG',
  wms_external: 'WMS',
  points_api: 'points',
  lines_api: 'lines',
  polygons_api: 'areas',
};

export function kindLabel(kind: LayerKind): string {
  // A kind the API adds later must not render `undefined` into the pane; the raw value is
  // at least honest and greppable.
  return KIND_LABELS[kind] ?? String(kind);
}

/** The `source_org · kind` line under a layer name (§F5.2). */
export function sourceKindLine(layer: Pick<Layer, 'source_org' | 'kind'>): string {
  return `${layer.source_org} · ${kindLabel(layer.kind)}`;
}

/**
 * §F5.2 *Filter layers* — "live; matches name + source + description".
 *
 * The mockup matched `name + src + desc`, but §D5 has **no `description` field**: the only
 * free text a row carries is `note`, and only `unavailable` rows have one. So the haystack
 * is `name + source_org + kind label + domain name + note`, which keeps the mockup's three
 * matchable facets (what it is, who published it, what it says about itself) and adds the
 * domain — the word a reader is most likely to type when the accordion titles are the
 * domains. Logged in NOTES.
 *
 * `domainName` is passed in rather than looked up here so the predicate stays pure.
 */
export function layerHaystack(layer: Layer, domainName?: string): string {
  return [layer.name, layer.source_org, kindLabel(layer.kind), domainName ?? layer.domain, layer.note]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function matchesLayerQuery(layer: Layer, query: string, domainName?: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return layerHaystack(layer, domainName).includes(q);
}
